import React, { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useState } from 'react';

import { Box, InputGroup, rem, Text, Textarea } from '@vimeo/bokeh';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import { createDomName } from '@vimeo/live-core/utils/dom/identifying';

import { translations } from '../../../../translations';

function isQuestionValid(question: string): boolean {
  return (
    question.trim().length > 0 &&
    question.trim().length <= interactionToolsConfig.POLLS.MAX_QUESTION_LENGTH
  );
}

interface IPollQuestionFieldProps {
  onQuestionChange: ChangeEventHandler<HTMLTextAreaElement>;
  question: string;
  hasSubmitError: boolean;
  onValidate?: (isValid: boolean) => void;
}

export function PollQuestionField({
  onQuestionChange,
  question,
  hasSubmitError,
  onValidate,
}: IPollQuestionFieldProps) {
  const [isShowError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    setShowError(hasSubmitError);
  }, [hasSubmitError]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const isValid = isQuestionValid(event.target.value);
      setShowError(!isValid);
      onValidate?.(isValid);
      onQuestionChange(event);
    },
    [onQuestionChange, onValidate],
  );

  const isInvalid = isShowError && !isQuestionValid(question);

  return (
    <InputGroup display={'flex'} flexDirection={'column'} gap={rem(8)}>
      <Text variant="body-xl" fontSize={'text-sm'}>
        {translations.question}
      </Text>

      <Textarea
        className={createDomName('poll-form-question')}
        tabIndex={1}
        minHeight={rem(120)}
        maxHeight={rem(200)}
        resize={'vertical'}
        placeholder={translations.askYourViewersQuestion}
        value={question}
        isInvalid={isInvalid}
        onChange={onChange}
      />

      <Box position={'relative'} height={rem(12)}>
        {question.length ? (
          <Text
            variant={'body-sm'}
            position={'absolute'}
            width={'100%'}
            align={'right'}
            lineHeight={rem(14)}
            color={isInvalid ? 'status-destructive-primary' : 'text-secondary'}
          >
            {question.trim().length} / {interactionToolsConfig.POLLS.MAX_QUESTION_LENGTH}
          </Text>
        ) : null}
      </Box>
    </InputGroup>
  );
}
